import { useEffect } from 'react';

/**
 * Inyecta un contenedor GTM por ID: script en <head> + noscript en <body>
 */
const loadGTM = (gtmId) => {
  if (!gtmId) return;
  if (document.querySelector(`script[data-tracking-gtm="${gtmId}"]`)) return;

  const inlineScript = document.createElement('script');
  inlineScript.dataset.trackingGtm = gtmId;
  inlineScript.innerHTML = `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${gtmId}');
  `;
  document.head.appendChild(inlineScript);

  const noscript = document.createElement('noscript');
  noscript.dataset.trackingGtm = gtmId;
  noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
  document.body.insertBefore(noscript, document.body.firstChild);
};

/**
 * Inyecta GA4 (gtag.js) por measurement ID (G-XXXXXXXXXX)
 */
const loadGA = (gaId) => {
  if (!gaId) return;

  // gtag.js base script (solo una vez para todo gtag)
  if (!document.querySelector('script[data-tracking-gtag-base]')) {
    const baseScript = document.createElement('script');
    baseScript.async = true;
    baseScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    baseScript.dataset.trackingGtagBase = 'true';
    document.head.appendChild(baseScript);

    const initScript = document.createElement('script');
    initScript.dataset.trackingGtagInit = 'true';
    initScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      window.gtag = gtag;
      gtag('js', new Date());
    `;
    document.head.appendChild(initScript);
  }

  // config para este ID concreto
  if (document.querySelector(`script[data-tracking-ga="${gaId}"]`)) return;
  const configScript = document.createElement('script');
  configScript.dataset.trackingGa = gaId;
  configScript.innerHTML = `
    (function(){
      var run = function(){
        if (typeof window.gtag === 'function') {
          window.gtag('config', '${gaId}');
        } else {
          setTimeout(run, 100);
        }
      };
      run();
    })();
  `;
  document.head.appendChild(configScript);
};

/**
 * Inyecta Meta/Facebook Pixel por ID
 */
const loadMetaPixel = (pixelId) => {
  if (!pixelId) return;

  // Base fbq script (solo una vez)
  if (!document.querySelector('script[data-tracking-fbq-base]')) {
    const baseScript = document.createElement('script');
    baseScript.dataset.trackingFbqBase = 'true';
    baseScript.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
    `;
    document.head.appendChild(baseScript);
  }

  if (document.querySelector(`script[data-tracking-pixel="${pixelId}"]`)) return;
  const initScript = document.createElement('script');
  initScript.dataset.trackingPixel = pixelId;
  initScript.innerHTML = `
    (function(){
      var run = function(){
        if (typeof window.fbq === 'function') {
          window.fbq('init', '${pixelId}');
          window.fbq('track', 'PageView');
        } else {
          setTimeout(run, 100);
        }
      };
      run();
    })();
  `;
  document.head.appendChild(initScript);

  // noscript fallback
  if (!document.querySelector(`noscript[data-tracking-pixel="${pixelId}"]`)) {
    const noscript = document.createElement('noscript');
    noscript.dataset.trackingPixel = pixelId;
    noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1" alt="Meta Pixel tracking" title="Meta Pixel tracking"/>`;
    document.body.appendChild(noscript);
  }
};

/**
 * TrackingScripts: inyecta dinámicamente todos los IDs de tracking provistos por Strapi.
 *
 * Espera una lista (companyInfo.trackingIds) con objetos { type, trackingId, enabled }.
 * Si la lista está vacía o no existe, no monta nada.
 *
 * type soportados: 'gtm' | 'ga' | 'meta-pixel'
 */
const TrackingScripts = ({ trackingIds }) => {
  useEffect(() => {
    if (!Array.isArray(trackingIds) || trackingIds.length === 0) return;

    trackingIds.forEach((entry) => {
      if (!entry) return;
      if (entry.enabled === false) return;
      const id = (entry.trackingId || '').trim();
      if (!id) return;

      switch (entry.type) {
        case 'gtm':
          loadGTM(id);
          break;
        case 'ga':
          loadGA(id);
          break;
        case 'meta-pixel':
          loadMetaPixel(id);
          break;
        default:
          break;
      }
    });
  }, [trackingIds]);

  return null;
};

export default TrackingScripts;
